import { historyAPI } from './api';

export interface GenerationHistory {
  id: string;
  user_id: string;
  type: string;
  category: string;
  prompt: string;
  input_images?: string[] | null;
  tags?: string[] | null;
  output_url?: string;
  thumbnail_url?: string;
  status: string;
  error_message?: string;
  settings?: any;
  credits_used?: number;
  task_id?: string;
  created_at: string;
  expires_at?: string;
}

/** 解析 JSON 字段（兼容字符串 / 数组） */
function parseJsonArray(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizeHistoryItem(row: any): GenerationHistory {
  return {
    ...row,
    input_images: parseJsonArray(row.input_images),
    tags: parseJsonArray(row.tags),
  };
}

export type HistoryQueryOptions = {
  category?: string | string[];
  limit?: number;
  q?: string;
  tag?: string;
};

// 获取用户历史记录
export async function getUserHistory(
  userId: string,
  categoryOrOptions?: string | string[] | HistoryQueryOptions,
  limit: number = 50
): Promise<{ history: GenerationHistory[]; error: string | null }> {
  try {
    let options: HistoryQueryOptions = { limit };
    if (typeof categoryOrOptions === 'string' || Array.isArray(categoryOrOptions)) {
      options = { category: categoryOrOptions, limit };
    } else if (categoryOrOptions && typeof categoryOrOptions === 'object') {
      options = { limit: 50, ...categoryOrOptions };
    }
    const history = await historyAPI.getUserHistory(userId, options);
    return { history: (history || []).map(normalizeHistoryItem), error: null };
  } catch (error: any) {
    console.error('获取历史记录失败:', error);
    return { history: [], error: error.message || '获取历史记录失败' };
  }
}

// 创建历史记录
export async function createHistory(userId: string, data: Partial<GenerationHistory>): Promise<{ success: boolean; error: string | null; id?: string }> {
  try {
    const response = await historyAPI.createHistory({
      user_id: userId,
      ...data
    });
    return { success: response.success, error: null, id: response.id };
  } catch (error: any) {
    console.error('创建历史记录失败:', error);
    return { success: false, error: error.message || '创建历史记录失败' };
  }
}

// 更新历史记录
export async function updateHistory(historyId: string, updates: Partial<GenerationHistory>): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await historyAPI.updateHistory(historyId, updates);
    return { success: response.success, error: null };
  } catch (error: any) {
    console.error('更新历史记录失败:', error);
    return { success: false, error: error.message || '更新历史记录失败' };
  }
}

// 通过任务ID更新历史记录
export async function updateHistoryByTaskId(userId: string, taskId: string, updates: Partial<GenerationHistory>): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await historyAPI.updateHistoryByTaskId(userId, taskId, updates);
    return { success: response.success, error: null };
  } catch (error: any) {
    console.error('更新历史记录失败:', error);
    return { success: false, error: error.message || '更新历史记录失败' };
  }
}

// 删除历史记录
export async function deleteHistory(historyId: string): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await historyAPI.deleteHistory(historyId);
    if (!response?.success) {
      return { success: false, error: '删除失败' };
    }
    return { success: true, error: null };
  } catch (error: any) {
    console.error('删除历史记录失败:', error);
    return { success: false, error: error.message || '删除历史记录失败' };
  }
}

// 清理过期历史记录
export async function cleanupExpiredHistory(): Promise<{ success: boolean; error: string | null }> {
  try {
    return { success: true, error: null };
  } catch (error: any) {
    console.error('清理过期历史记录失败:', error);
    return { success: false, error: error.message || '清理过期历史记录失败' };
  }
}

// 删除多条历史记录
export async function deleteMultipleHistory(historyIds: string[]): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await historyAPI.deleteMultipleHistory(historyIds);
    if (!response?.success) {
      return { success: false, error: '批量删除失败' };
    }
    return { success: true, error: null };
  } catch (error: any) {
    console.error('删除多条历史记录失败:', error);
    return { success: false, error: error.message || '批量删除历史记录失败' };
  }
}

/** 是否为视频类素材 */
export function isVideoHistoryCategory(category: string) {
  return category === 'video' || category === 'sora2-video' || category === 'veo31-video';
}
